// Considered: Atlas Node row (Node.tsx:723-783) — rejected: welded to AtlasViewContext + columnsStyle + StatusPopoverMolecule, not extractable.
// Considered: SphereResultRowMolecule — rejected: uses ScopeCoinAtom + StatusIcon (wrong atoms), single-consumer. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import type { GlyphColor } from '../../../types/glyph';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import type { StatusValue } from '../../atoms/status-badge/StatusBadgeAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './SphereRowMolecule.scss';

// 'match' is intentionally omitted — no chip rendered for match reason.
const REASON_CHIP: Record<string, { icon: string; labelKey: string }> = {
  recent: { icon: 'lucide:history', labelKey: 'sphereRow.reason.recent' },
  mine: { icon: 'lucide:user', labelKey: 'sphereRow.reason.mine' },
  nearby: { icon: 'lucide:circle-dot', labelKey: 'sphereRow.reason.nearby' },
};

export type SphereRowVariant = 'compact' | 'table';
export type SphereRowReason = 'recent' | 'mine' | 'nearby' | 'match';

export interface SphereRowCenterInfo {
  sphereId?: string;
  sphereName?: string;
  imageId?: string;
  glyph?: string | null;
  glyphColor?: GlyphColor;
}

export interface SphereRowMoleculeProps {
  variant: SphereRowVariant;
  sphereId?: string;
  sphereName?: string;
  imageId?: string;
  glyph?: string | null;
  glyphColor?: GlyphColor;
  isPrivate?: boolean;
  /** Table variant: index status vocab string (on_track / at_risk / needs_attention / critical / blocked) */
  status?: string | null;
  /** Table variant only */
  description?: string | null;
  /** Table variant only */
  center?: SphereRowCenterInfo | null;
  /** Compact variant: ancestor names joined as breadcrumb */
  parentPath?: string[];
  /** Caller-provided trailing slot; compact variant uses it for scope buttons; table variant uses it for highlight chips */
  trailing?: React.ReactNode;
  reason?: SphereRowReason | null;
  ambiguous?: boolean;
  focused?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onFocus?: () => void;
  className?: string;
}

const SphereRowMolecule: React.FC<SphereRowMoleculeProps> = ({
  variant,
  sphereId,
  sphereName,
  imageId,
  glyph,
  glyphColor,
  isPrivate,
  status,
  description,
  center,
  parentPath = [],
  trailing,
  reason,
  ambiguous = false,
  focused = false,
  selected = false,
  onSelect,
  onFocus,
  className = '',
}) => {
  const { t } = useTranslation();

  const canonicalStatus: StatusValue = (status && ['on_track', 'at_risk', 'needs_attention', 'critical', 'blocked'].includes(status))
    ? status as StatusValue
    : 'unknown';
  const reasonConfig = reason && reason !== 'match' ? REASON_CHIP[reason] ?? null : null;

  const rowClasses = [
    'sphere-row-molecule',
    `sphere-row-molecule--${variant}`,
    focused ? 'sphere-row-molecule--focused' : '',
    selected ? 'sphere-row-molecule--selected' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleKeyDown = onSelect
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }
    : undefined;

  if (variant === 'compact') {
    return (
      <div
        role="option"
        aria-selected={focused || selected}
        tabIndex={-1}
        className={rowClasses}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        data-testid="sphere-row-molecule"
      >
        <div className="sphere-row-molecule__compact-identity">
          <SphereIdentityAtom
            sphereId={sphereId}
            sphereName={sphereName}
            imageId={imageId}
            glyph={glyph ?? undefined}
            glyphColor={glyphColor}
            isPrivate={isPrivate}
            size="small"
          />
          {ambiguous && (
            <span className="sphere-row-molecule__ambiguous-tag">
              {t('sphereRow.ambiguousTag')}
            </span>
          )}
          {reasonConfig && (
            <ChipAtom
              label={t(reasonConfig.labelKey)}
              icon={reasonConfig.icon}
              className="sphere-row-molecule__reason-chip"
            />
          )}
        </div>
        {parentPath.length > 0 && (
          <span className="sphere-row-molecule__breadcrumb" data-testid="sphere-row-breadcrumb">
            {parentPath.join(' › ')}
          </span>
        )}
        {trailing !== undefined && (
          <span className="sphere-row-molecule__trailing">{trailing}</span>
        )}
      </div>
    );
  }

  return (
    <div
      role="option"
      aria-selected={focused || selected}
      tabIndex={-1}
      className={`${rowClasses} sphere-row-grid`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      data-testid="sphere-row-molecule"
    >
      <div className="sphere-row-molecule__identity-cell">
        <SphereIdentityAtom
          sphereId={sphereId}
          sphereName={sphereName}
          imageId={imageId}
          glyph={glyph ?? undefined}
          glyphColor={glyphColor}
          isPrivate={isPrivate}
        />
        {ambiguous && (
          <span className="sphere-row-molecule__ambiguous-tag">
            {t('sphereRow.ambiguousTag')}
          </span>
        )}
        {reasonConfig && (
          <ChipAtom
            label={t(reasonConfig.labelKey)}
            icon={reasonConfig.icon}
            className="sphere-row-molecule__reason-chip"
          />
        )}
      </div>

      <div className="sphere-row-molecule__status-cell" data-testid="sphere-row-status">
        <StatusBadgeAtom status={canonicalStatus} variant="sparkle" size={20} />
      </div>

      <div className="sphere-row-molecule__description-cell" data-testid="sphere-row-description">
        {description && (
          <span className="sphere-row-molecule__description">{description}</span>
        )}
      </div>

      <div className="sphere-row-molecule__center-cell" data-testid="sphere-row-center">
        {center && (
          <SphereIdentityAtom
            sphereId={center.sphereId}
            sphereName={center.sphereName}
            imageId={center.imageId}
            glyph={center.glyph ?? undefined}
            glyphColor={center.glyphColor}
            size="small"
          />
        )}
      </div>
      {trailing !== undefined && (
        <div className="sphere-row-molecule__trailing">{trailing}</div>
      )}
    </div>
  );
};

export default SphereRowMolecule;
